<template>
	<div :class="'configuration-panel animated fast fadeIn'">
		<div v-if="selectedPollStatus.loading">
			<spinner hideText="true"></spinner>
		</div>
		<div v-else>
			<div class="configuration-panel-body" v-if="!pollUpdateStatus.success">

				<form @submit="updateItem" action="javascript:void(0);">
					<section>
					<header class="configuration-panel-header">
						<h1 class="header-title">Edit Integration</h1>
						<span class="header-actions">
							<button type="button" class="link" @click="requestClose">
								<i class="fas fa-times fa-lg"></i>
							</button>
						</span>
					</header>
					<section>
						<templateOptions :options="pollUpdate.options" :updateFunction="updatePollUpdateValue" :toggleSecretUpdate="toggleSecretUpdate" :showHelper="true" :model="pollUpdate"></templateOptions>
					</section>
				</section>

				<section>
					<header class="configuration-panel-header">
						<h1 class="header-title">Status</h1>
					</header>
					<div class="input-group">
						<select v-model="pollUpdate.state">
							<option disabled value=''>Change Status</option>
							<option v-for="state in stateOptions" v-bind:value="state.value" v-bind:key=state.value>
								{{ state.display }}
							</option>
						</select>
					</div>
				</section>
				<section class="bucket-info option">
					<header class="configuration-panel-header">
						<h1 class="header-title">Initial State Bucket</h1>
					</header>
					<div class="input-group">
						<select v-model="selectedBucket" name="Initial State Bucket">
							<option disabled value=''>Choose a Bucket</option>
							<option v-for="bucket in buckets" v-bind:value="bucket" v-bind:key=bucket.id>
								{{ bucket.name }}
							</option>
						</select>
					</div>
				</section>
				<section v-if="!selectedPoll.marketItem.uiOptions.signalSelectionDisabled">
					<header class="configuration-panel-header" v-if="selectedPoll.marketItem.signalOptions">
						<h1 class="header-title">Choose Data to Use For Signal Keys</h1>
					</header>
					<section v-if="selectedPoll.marketItem.signalOptions">
						<div class="input-group">
							<label class="radio"><input type="radio" id="all" name="dataToUse" value="all" v-model="useSignals"> All available data  </label>
							<label class="radio"><input type="radio" id="selection" name="dataToUse" value="some" v-model="useSignals"> I want to select specific data</label>
						</div>
						<section v-if="useSignals === 'some'">
							<div class="input-group signalKey-list" v-for="signal in sortedSignalOptions" :key="signal.name || signal.group">
								<label><input type="checkbox" v-model="pollUpdate.selectedSignals" :value="signal.name || signal.group"/>{{signal.name || signal.group}}</label>
							</div>
						</section>
					</section>
				</section>
				<section>
					<header class="configuration-panel-header">
						<h1 class="header-title">How often do you want this data to refresh?</h1>
					</header>
					<section>
						<div class="input-group" v-for="(option, idx) in selectedPoll.marketItem.cronSelections" v-bind:key="option.cronExpr">
							<label :for="idx" class="radio"><input type="radio" :id="idx" v-bind:value="option" v-model="selectedCron">{{selectedPoll.marketItem.cronReadable(option.cronExpr)}} <span class="token"></span> {{option.cost}}</label>
						</div>
					</section>
				</section>
				<section>
					<button type="submit" class="outline-btn" :disabled="pollUpdateStatus.loading">Save Integration<spinner v-if="pollUpdateStatus.loading" hideText="true"></spinner></button>
				</section>
				</form>
				<div v-if="pollUpdateStatus.error" class="fail-message">
					<section>
						<h3 v-if="pollUpdateStatus.errorMessage">{{ pollUpdateStatus.errorMessage }}</h3>
						<h3 v-else>Something went wrong and your integration could not be updated.</h3>
						You can try again, or <a href="https://support.initialstate.com/hc/en-us/requests/new" target="_blank"> contact us </a> for help!
					</section>
				</div>
			</div>
			<div v-if="pollUpdateStatus.success" class="text-center success-message">
				<div>
					<AnimatedLogo></AnimatedLogo>
				</div>
				<h3>Your Integration Has Been Updated!</h3>
				<p><a :href="getIotURL()" class="outline-btn call-to-action" target="_blank">View in the IoT App</a></p>
				<ul class="success-links">
					<li>
						<router-link :to="{name: 'marketplace'}" class="arrow-link" >
							Browse More Integrations
						</router-link>
					</li>
					<li>
						<router-link :to="{name: 'manage'}" class="arrow-link" >
							Go To Your Installed Integrations
						</router-link>
					</li>
				</ul>
			</div>

		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import Spinner from './Spinner';
import { mapGetters } from 'vuex';
import TemplateOptions from './TemplateOptions';
import Details from './Details';
import AnimatedLogo from './AnimatedLogo';
import { validateTemplateOptions } from '../utils/validator';

const SECRET_PLACEHOLDER = '*'.repeat(20);

export default {
	name: 'ItemUpdate',
	components: {
		Spinner,
		TemplateOptions,
		AnimatedLogo,
	},
	data () {
		return {
			fadeOut: false,
			selectedBucket: null,
			pollUpdate: {},
			newAccessKey: null,
			newBucketKey: null,
			selectedCron: null,
			useSignals: 'all',
			stateOptions: [{value: 'INACTIVE', display: 'Inactive'}, {value: 'ACTIVE', display: 'Active'}]
		};
	},
	computed: {
		...mapGetters([
			'appURL',
			'buckets',
			'selectedPoll',
			'selectedPollStatus',
			'pollUpdateStatus',
			'roles',
			'credits',
			'user',
		]),
		sortedSignalOptions() {
			return [...this.selectedPoll.marketItem.signalOptions].sort((a,b) => {
				// uppercase to ignore casing
				const itemA = (a.name || a.group).toUpperCase();
				const itemB = (b.name || b.group).toUpperCase();

				let comparison = 0;
				if (itemA > itemB) {
					comparison = 1;
				} else if (itemA < itemB) {
					comparison = -1;
				}
				return comparison;
			});
		}
	},
	watch: {
		selectedPoll: function() {
			this.setup();
			this.matchBucket();
		},
		buckets: function() {
			this.matchBucket();
		},
		selectedBucket: function() {
			this.newAccessKey = null;
			this.newBucketKey = null;
			if (this.selectedBucket) {
				if (this.selectedBucket.accessKey !== this.selectedPoll.accessKey) {
					this.newAccessKey = this.selectedBucket.accessKey;
				}
				if (this.selectedBucket.bucketKey !== this.selectedPoll.bucketKey) {
					this.newBucketKey = this.selectedBucket.bucketKey;
				}
			}
		}
	},
	methods: {
		setup: function() {
			if (!this.selectedPoll) {
				return;
			}

			let poll = this.selectedPoll;

			// merge options and values (secret and overt)
			let options = poll.marketItem.templateOptions || {};
			let currValues = poll.values;
			let merged = this.merge(options, currValues, poll.secretsOptions);
			let displayOptions = this.disableSecretOptions(merged);

			this.pollUpdate.selectedSignals = [];
			if (poll.selectedSignals) {
				this.pollUpdate.selectedSignals = [...poll.selectedSignals];
				this.useSignals = 'some';
			} else {
				this.useSignals = 'all';
			}

			this.pollUpdate.options = Object.assign({},  displayOptions);
			this.pollUpdate.state = poll.state;

			// select current cron option
			if (this.selectedPoll.marketItem.cronSelections) {
				let idx = this.selectedPoll.marketItem.cronSelections.findIndex((selection) => {
					return selection.cronExpr === this.selectedPoll.cron;
				});
				if (idx >= 0) {
					this.selectedCron = this.selectedPoll.marketItem.cronSelections[idx];
				}
			}
		},
		matchBucket: function() {
			if (!this.selectedPoll || !this.buckets) {
				return;
			}
			let poll = this.selectedPoll;
			let matchedBucket = this.buckets.find(b => {
				return b.accessKey === poll.accessKey && b.bucketKey === poll.bucketKey;
			});
			if (matchedBucket) {
				this.selectedBucket = matchedBucket;
			}
		},
		disableSecretOptions: function(options) {
			// disable secret options from being updatable
			for (let key in options) {
				if (options.hasOwnProperty(key)) {
					let optArr = options[key];
					optArr.forEach(optItem => {
						if (optItem.secret) {
							Vue.set(optItem, 'disabled', true);
						}
					});
				}
			}
			return options;
		},
		toggleSecretUpdate: function(e, opt, idx) {
			// toggle a secret option to be updatable
			let secretOption = this.pollUpdate.options[opt][idx];
			if (secretOption.disabled) {
				secretOption.value = '';
				secretOption.disabled = false;
			} else {
				secretOption.value = SECRET_PLACEHOLDER;
				secretOption.disabled = true;
			}
		},
		merge(base, newVals, secretsUsed = {}) {
			let merged = Object.assign({}, base);
			for (let key in newVals) {
				if (newVals.hasOwnProperty(key)) {
					let newArr = newVals[key];
					let baseArr = merged[key] ? merged[key] : [];
					for (let newItem of newArr) {
						let match = null;
						match = baseArr.find(element => {
							let checkName = newItem.name;
							if (key === 'uri') {
								return element.name === newItem.name;
							} else if (key === 'headers') {
								return element.header === newItem.header && (checkName ? element.name === newItem.name : true);
							} else if (key === 'qs') {
								return element.param === newItem.param && (checkName ? element.name === newItem.name : true);
							}
						});
						if (match) {
							match.value = newItem.value;
						}
					}
				}
			}

			for (let key in merged) {
				if (merged.hasOwnProperty(key)) {
					let mergedArr = merged[key];
					let secretsUsedArr = secretsUsed[key] ? secretsUsed[key] : [];
					for (let secretsItem of mergedArr) {
						if (!secretsItem.secret) {
							continue;
						}
						// check if they have a saved secret value
						let secretsMatch = secretsUsedArr.find(element => {
							let checkName = secretsItem.name;
							if (key === 'uri') {
								return element.name === secretsItem.name;
							} else if (key === 'headers') {
								return element.header === secretsItem.header && (checkName ? element.name === secretsItem.name : true);
							} else if (key === 'qs') {
								return element.param === secretsItem.param && (checkName ? element.name === secretsItem.name : true);
							}
						});
						if (secretsMatch) {
							secretsItem.value = SECRET_PLACEHOLDER;
						}
					}
				}
			}

			return merged;
		},
		getOptionItem: function(opt, idx) {
			let optArrItem = this.pollUpdate.options[opt][idx];
			return optArrItem;
		},
		requestClose: function () {
			this.$emit('close');
		},
		updatePollUpdateValue (e, valuesOption, valueListIndex) {
			this.pollUpdate.options[valuesOption][valueListIndex].value = e.target.value;

		},
		updateItem() {
			if (!validateTemplateOptions(this.pollUpdate.options)) {
				return;
			}

			let update = {
				pollId: this.selectedPoll.id,
				state: this.pollUpdate.state,
				values: Object.assign({}, this.pollUpdate.options),
				cron: this.selectedCron ? this.selectedCron.cronExpr : null,
				selectedSignals: this.useSignals === 'some' ? this.pollUpdate.selectedSignals : null
			};
			// remove any disabled secrets from being part of the update values
			for (let key in update.values) {
				if (update.values.hasOwnProperty(key)) {
					update.values[key]= update.values[key].filter(optItem => !(optItem.secret && optItem.disabled));
				}
			}
			if (this.newAccessKey) {
				update.accessKey = this.newAccessKey;
			}
			if (this.newBucketKey) {
				update.bucketKey = this.newBucketKey;
			}

			if (this.selectedCron && this.selectedCron.cost && update.state === 'ACTIVE') {
				// make sure user has enough credits
				let cost = this.selectedCron.cost - (this.selectedPoll.credits || 0); // we care about the cost difference
				let remainingCredits = this.credits.remaining || 0;
				if (cost > remainingCredits) {
					this.$store.dispatch('SET_POLL_UPDATE_REQUEST_ERROR', 'Not enough credits.');
					return;
				}
			}
			let payload = update;
			payload.withMarketItem = true;
			this.$store.dispatch('POLL_UPDATE_REQUEST', payload);
		},
		getIotURL() {
			if (this.selectedBucket && this.selectedBucket.id) {
				let bucketId = this.selectedBucket.id;
				return this.appURL + `&destination=/tiles/${bucketId}`;
			}
			return this.appURL;
		},
	},
	created() {
		this.$store.dispatch('LIST_BUCKETS_REQUEST');
		this.setup();
	}
};
</script>

<style scoped>
	.success-message{
		background-color: #edf0f3;
		/* background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='208' height='208' viewBox='0 0 800 800'%3E%3Cg fill='none' stroke='%23aec9d4' stroke-width='2'%3E%3Cpath d='M769 229L1037 260.9M927 880L731 737 520 660 309 538 40 599 295 764 126.5 879.5 40 599-197 493 102 382-31 229 126.5 79.5-69-63'/%3E%3Cpath d='M-31 229L237 261 390 382 603 493 308.5 537.5 101.5 381.5M370 905L295 764'/%3E%3Cpath d='M520 660L578 842 731 737 840 599 603 493 520 660 295 764 309 538 390 382 539 269 769 229 577.5 41.5 370 105 295 -36 126.5 79.5 237 261 102 382 40 599 -69 737 127 880'/%3E%3Cpath d='M520-140L578.5 42.5 731-63M603 493L539 269 237 261 370 105M902 382L539 269M390 382L102 382'/%3E%3Cpath d='M-222 42L126.5 79.5 370 105 539 269 577.5 41.5 927 80 769 229 902 382 603 493 731 737M295-36L577.5 41.5M578 842L295 764M40-201L127 80M102 382L-261 269'/%3E%3C/g%3E%3Cg fill='%23aec9d4'%3E%3Ccircle cx='769' cy='229' r='8'/%3E%3Ccircle cx='539' cy='269' r='8'/%3E%3Ccircle cx='603' cy='493' r='8'/%3E%3Ccircle cx='731' cy='737' r='8'/%3E%3Ccircle cx='520' cy='660' r='8'/%3E%3Ccircle cx='309' cy='538' r='8'/%3E%3Ccircle cx='295' cy='764' r='8'/%3E%3Ccircle cx='40' cy='599' r='8'/%3E%3Ccircle cx='102' cy='382' r='8'/%3E%3Ccircle cx='127' cy='80' r='8'/%3E%3Ccircle cx='370' cy='105' r='8'/%3E%3Ccircle cx='578' cy='42' r='8'/%3E%3Ccircle cx='237' cy='261' r='8'/%3E%3Ccircle cx='390' cy='382' r='8'/%3E%3C/g%3E%3C/svg%3E"); */
	}
	.configuration-panel form > section{
		padding: 12px 15px;
		border-bottom: 1px solid #fff;
		margin-bottom:10px;
	}

	.configuration-panel form > section:last-of-type{

		border-bottom: none;

	}

	.header-actions{
		position: relative;
		top:-12px;
	}
	.configuration-panel-header {
		margin-bottom:10px;
		flex: 0 0 auto;
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.input-columns{
		display:flex;
		align-items: baseline;
		justify-content: space-between
	}

	header.input-columns {
		padding: 10px 0;
		text-align: center;
	}
	.input-columns > div:last-of-type{
		margin-right: 40%;
	}

	.input-columns .input-group{
		margin: 7px 0;
	}
	.outline-btn:hover .loading{
		color: white;
	}
	.outline-btn .loading{
		color: #337CCA;
		margin-left: 5px;
		display:inline-block;
		padding:0;
	}

	.success-links li{
		list-style: none;
		margin: 10px auto;
	}
	.configuration-panel {
		border-left: 1px solid #f1f3f6;
		padding: 10px 0;
		background-color: #EDF0F3;
		background-repeat: repeat;
		height: auto;
		height:100%;
		overflow: auto;
		margin-left: 20px;
		display: flex;
		flex-direction: column;
		box-shadow: -2px 5px 20px -12px #e3e6ea;
	}
	.header-title {
		flex: 1 1 auto;
		font-size: 1.2em;
		margin: 0;
	}
	.header-actions {
		flex: 0 0 auto;
	}
	.input-group small{
		margin-left:5px;
	}

	@-webkit-keyframes fadeIn {
		from {
			opacity: 0;
		}

		to {
			opacity: 1;
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}

		to {
			opacity: 1;
		}
	}

	.fadeIn {
		-webkit-animation-name: fadeIn;
		animation-name: fadeIn;
	}

	.animated {
		-webkit-animation-duration: 1s;
		animation-duration: 1s;
		-webkit-animation-fill-mode: both;
		animation-fill-mode: both;
	}

	.animated.fast {
		-webkit-animation-duration: 400ms;
		animation-duration: 400ms;
	}

	@media (print), (prefers-reduced-motion: reduce) {
		.animated {
			-webkit-animation-duration: 1ms !important;
			animation-duration: 1ms !important;
			-webkit-transition-duration: 1ms !important;
			transition-duration: 1ms !important;
			-webkit-animation-iteration-count: 1 !important;
			animation-iteration-count: 1 !important;
		}
	}

	@-webkit-keyframes fadeOut {
		from {
			opacity: 1;
		}

		to {
			opacity: 0;
		}
	}

	@keyframes fadeOut {
		from {
			opacity: 1;
		}

		to {
			opacity: 0;
		}
	}

	.fadeOut {
		-webkit-animation-name: fadeOut;
		animation-name: fadeOut;
	}

	@-webkit-keyframes fadeInDown {
		from {
			opacity: 0;
			-webkit-transform: translate3d(0, -100%, 0);
			transform: translate3d(0, -100%, 0);
		}

		to {
			opacity: 1;
			-webkit-transform: translate3d(0, 0, 0);
			transform: translate3d(0, 0, 0);
		}
	}

	@keyframes fadeInDown {
		from {
			opacity: 0;
			-webkit-transform: translate3d(0, -100%, 0);
			transform: translate3d(0, -100%, 0);
		}

		to {
			opacity: 1;
			-webkit-transform: translate3d(0, 0, 0);
			transform: translate3d(0, 0, 0);
		}
	}


	@-webkit-keyframes fadeOut {
		from {
			opacity: 1;
		}

		to {
			opacity: 0;
		}
	}

	@keyframes fadeOut {
		from {
			opacity: 1;
		}

		to {
			opacity: 0;
		}
	}

	.fadeOut {
		-webkit-animation-name: fadeOut;
		animation-name: fadeOut;
	}

	label{
		display: block;
	}

	.input-group{
		margin: 15px auto;
	}

	.input-group input[type = radio],
	.input-group input[type = checkbox]{
		vertical-align: middle;
		margin-right:5px;
	}

	.input-group input[type = text],
	.input-group select {
		display:block;
		width:100%;

	}
	.input-helper{
		margin: 0 0 15px;
		font-size:12px;
		color: #555;
	}
	label.radio{
		margin-bottom: 5px;
	}

	.success-message{
		display: flex;
		flex-direction: column;
		height: 100%;
		padding-top:25px;
	}

	.success-message svg{
		margin: 25px auto;
	}


	/* // Extra small devices (portrait phones, less than 576px) */
	@media (max-width: 575.98px) {
		.configuration-panel{
			margin-left: 0;
		}


		}

	/* // Small devices (landscape phones, 576px and up) */
	@media (min-width: 576px) and (max-width: 767.98px) {

	.configuration-panel{
		margin-left: 0;
	}
	}


	/* // Medium devices (tablets, 768px and up) */
	@media (min-width: 768px) and (max-width: 991.98px) {
		.visible-xs, .visible-sm{
			display:none;
		}
	}

	/* // Large devices (desktops, 992px and up) */
	@media (min-width: 992px) and (max-width: 1199.98px) {
		.visible-xs, .visible-sm{
			display:none;
		}
	}

	/* // Extra large devices (large desktops, 1200px and up) */
	@media (min-width: 1200px) {
		.visible-xs, .visible-sm{
			display:none;
		}

	}
	</style>



// WEBPACK FOOTER //
// src/components/ItemUpdate.vue